import { DeepDiveWidget } from '@/core/widgets/export';
import { Permissions } from '@/core/permissions/export';
import {
  RAIL_HIDDEN_MARKER,
  RAIL_VISIBLE_MARKER,
  RESERVED_SIDE_PANEL_IDS,
  SidePanelId,
} from '@/core/deep-dive/contexts/side-panel-provider';

const RESERVED_APP_IDS = [RAIL_VISIBLE_MARKER, RAIL_HIDDEN_MARKER];

// Deliberately separate from WidgetRegistry: deep-dive apps need reserved-id
// validation against hash-routing markers and permission-based visibility
// gating that dashboard/tab widgets don't, so bolting them onto the generic
// registry would mean stretching its API to fit a different set of rules.
const registry = new Map<string, DeepDiveWidget>();

const SHOULD_HIDE_TO_USER: keyof DeepDiveWidget = 'shouldHideToUser';
const HAS_PERMISSION: keyof DeepDiveWidget = 'hasPermission';
function withDeepDiveAccessGate(widget: DeepDiveWidget): DeepDiveWidget {
  return new Proxy(widget, {
    get(target, prop) {
      if (prop === SHOULD_HIDE_TO_USER) {
        return (permissions: string[]) =>
          !permissions.includes(Permissions.DEEP_DIVE_ACCESS) ||
          !!target.shouldHideToUser?.(permissions);
      }
      if (prop === HAS_PERMISSION) {
        return async (permissions: string[]) => {
          if (!permissions.includes(Permissions.DEEP_DIVE_ACCESS)) return false;
          return target.hasPermission(permissions);
        };
      }
      return Reflect.get(target, prop, target);
    },
  });
}

export function registerDeepDiveApp(
  widget: DeepDiveWidget,
  { requireDeepDiveAccess = true }: { requireDeepDiveAccess?: boolean } = {},
): void {
  if (RESERVED_SIDE_PANEL_IDS.includes(widget.id as SidePanelId)) {
    console.error(
      `registerDeepDiveApp: "${widget.id}" is reserved for a top-level panel — choose a different id. Skipping registration.`,
    );
    return;
  }

  if (RESERVED_APP_IDS.includes(widget.id)) {
    console.error(
      `registerDeepDiveApp: "${widget.id}" is reserved for hash routing — choose a different id. Skipping registration.`,
    );
    return;
  }

  if (registry.has(widget.id)) {
    console.error(
      `registerDeepDiveApp: "${widget.id}" is already registered — replacing the previous entry.`,
    );
  }

  registry.set(widget.id, requireDeepDiveAccess ? withDeepDiveAccessGate(widget) : widget);
}

export function unregisterDeepDiveApp(id: string): void {
  registry.delete(id);
}

export function getDeepDiveApps(): DeepDiveWidget[] {
  return Array.from(registry.values());
}

export function getDeepDiveApp(id: string): DeepDiveWidget | undefined {
  return registry.get(id);
}

export function isDeepDiveAppVisible(widget: DeepDiveWidget, permissions: string[]): boolean {
  return !widget.shouldHideToUser?.(permissions);
}
